class CacheNode<Val> {
  constructor (public children: { [key: string]: CacheNode<Val> | CacheTerminalNode<Val> } = {}) {}
}

class CacheTerminalNode<Val> {
  constructor (public val: Val) {}
}

export class AsyncComputedProperty<Val, Args extends unknown[]> {
  private cachedData = new CacheNode<Val>()
  private lastDebounced: Args = null

  constructor (
    private computeResult: (args: Args) => Promise<Val>,
    private cacheUpdatedCb: () => void = () => void 0,
    private debounce: number = 0
  ) {}

  readOrAsyncCompute (args: Args, lazy = false) {
    const terminalNode = this.readComputedProperty(args)
    if (terminalNode) return terminalNode.val

    // in lazy mode, we do not try to compute the result if not in cache
    if (!lazy) void this.notifyArgsChanged(args)
    return null
  }

  private async notifyArgsChanged (args: Args) {
    // start the fetching, store an empty node to prevent multiple fetching for the same args
    this.writeComputedProperty(null, args)
    
    if (this.debounce) {
      this.lastDebounced = args
      await new Promise(resolve => setTimeout(resolve, window.env === 'test' ? 0 : this.debounce))
      if (this.lastDebounced !== args) return this.removeComputedProperty(args)
    }

    this.writeComputedProperty(await this.computeResult(args), args)
    this.cacheUpdatedCb()
    this.lastDebounced = null
  }

  private readComputedProperty (args: Args): CacheTerminalNode<Val> {
    let cachedData: CacheNode<Val> | CacheTerminalNode<Val> = this.cachedData

    for (const arg of args) {
      const key = this.getCacheKeyForArg(arg)
      if (cachedData instanceof CacheNode) cachedData = cachedData.children[key]
    }

    return cachedData instanceof CacheTerminalNode ? cachedData : null
  }

  private writeComputedProperty (val: Val, args: Args) {
    let cachedData = this.cachedData

    for (const arg of args.slice(0, args.length - 1)) {
      const key = this.getCacheKeyForArg(arg)
      if (cachedData.children[key] instanceof CacheNode) cachedData = cachedData.children[key] as CacheNode<Val>
      else if (typeof cachedData.children[key] === 'undefined') cachedData = cachedData.children[key] = new CacheNode<Val>()
      else throw new Error('Unexpected node type in cache')
    }

    const key = this.getCacheKeyForArg(args[args.length - 1])
    if (cachedData.children[key] instanceof CacheTerminalNode) (cachedData.children[key] as CacheTerminalNode<Val>).val = val
    else cachedData.children[key] = new CacheTerminalNode(val)
  }

  private removeComputedProperty (args: Args) {
    let cachedData = this.cachedData

    for (const arg of args.slice(0, args.length - 1)) {
      const key = this.getCacheKeyForArg(arg)
      if (cachedData.children[key] instanceof CacheNode) cachedData = cachedData.children[key] as CacheNode<Val>
      else if (typeof cachedData.children[key] === 'undefined') cachedData = cachedData.children[key] = new CacheNode<Val>()
      else throw new Error('Unexpected node type in cache')
    }

    const key = this.getCacheKeyForArg(args[args.length - 1])
    if (cachedData) delete cachedData.children[key]
  }

  private getCacheKeyForArg (arg: unknown): string {
    switch (typeof arg) {
      case 'bigint':
      case 'boolean':
      case 'number':
      case 'string':
      case 'symbol':
        return arg.toString()
      
      case 'undefined':
        return 'undefined'
      
      case 'object':
        if (arg === null) return 'null'
        if ('id' in arg) return this.getCacheKeyForArg((arg as any).id)
        if (Array.isArray(arg)) return arg.map(val => this.getCacheKeyForArg(val)).join(':')
        return JSON.stringify(arg)

      default:
        throw new Error('Unsupported type of argument')
    }
  }
}
